'use client';
/*
 *  @providesModule MatchJoinController
 *
 */
import {
  APIUtilities,
  AWSManager,
  ClientPropertyConstants,
  CurrentUserStore,
  ErrorDialogModal,
  GlobalEventEmitter,
  LeagueType,
  SaveAccountFlowController,
  LiveEventFTUEFlowController,
  NavigationManager,
  // LocationPermissionController,
  GeocomplyController,
  NetClient,
  setTrophyDataForStorage,
  SKZMatch,
  SKZMatchPlayer,
  SKZTournament,
  writeLeaguePlacement,
  ModalNavigationController,
  StaticWarningModal,
  LoadingIndicator,
  SyncMatchMakingController,
  _m,
  SkillzRouter,
  RESTStore,
  PlayableMatchesStore,
  SKZBracket,
  SKZUser,
  renderLiveBracketAutoplayDialog,
  isLiveBracketTournamentReadyToAutoplay,
  PLOWDisplayHandler,
  I18n,
  TrackMetricPerformance,
} from '@sdk-mobile';
// @ts-ignore
import SKZConstants from 'SKZConstants';
import {
  hasEnoughBalance,
  isPooledLiquidity,
  isThreePlusH2H,
  isTournamentJoinable,
} from '@/services/internal/tournament/helpers';
import {
  getMinimumAge,
  isAccountRequired,
  isEmailRequired,
  isPhoneRequired,
} from '@/services/internal/tournament/join/guards';
import {
  clearJoinSession,
  getJoinSession,
  isAlreadyJoining,
  setJoinSession,
  TournamentJoinGuards,
  TournamentJoinOptions,
} from '@/services/internal/tournament/join/session';
import {getUserId} from '@/services/internal/user/getUserId';
import {getDeviceInfo} from '@/services/internal/device/getDeviceInfo';
import {DeviceInfo, PlayerMetadata, Tournament} from '@/services/internal/tournament/types';
import Preferences from '@/sdk-mobile-web/Preferences/Preferences';
//@ts-ignore
import {NativeModules} from 'react-native';
import OrientationTransitionController from '@/sdk-mobile-web/OrientationTransitionController/OrientationTransitionController';
import SKAdNetworkController from '@/sdk-mobile-web/SKAdNetworkController/SKAdNetworkController';
import Alert from '@/sdk-mobile-web/Alert/Alert';
import {noop, cloneDeep} from 'lodash';
import moment from 'moment';

const SkillzModule = NativeModules.SkillzModule;
const CASH_MINIMUM_AGE = SKZConstants.SKZ_MINIMUM_CASH_PLAY_AGE;
const LegacyTournamentPlayerIdKey = 'tournamentPlayerIDSKZ';
const LegacyPlayerIdKey = 'playerIDSKZ';
const LegacyEntryIdKey = 'entryIDSKZ';
const LegacyTournamentIdKey = 'tourneyIDSKZ';
const INVALID_USER_SEGMENT = 'INVALID_USER_SEGMENT';
const GAME_ID = SkillzModule.App.GameId;

export const JOIN_ERRORS = {
  alreadyPlaying: 'JOIN_ALREADY_PLAYING_SYNC',
  noServerAvailable: 'NO_SYNC_SERVER_AVAILABLE',
  insufficientFunds: 'JOIN_INSUFFICIENT_FUND',
  parameterValidation: 'PARAMETER_VALIDATION_ERROR',
  needsRegistration: 'JOIN_NEEDS_FULL_REG',
  unavailable: 'JOIN_ERROR_UNAVAILABLE',
  emptyMatch: 'EMPTY_MATCH_OBJECT_JOIN_CALL_SKZ',
  cashDisabledLocation: 'JOIN_NO_CASH_LOCATION',
  tournamentFull: 'TOURNAMENT_FULL',
  vpnOrProxy: 'JOIN_VPN_PROXY',
  userNotFound: 'MATCHMAKER_USER_NOT_FOUND',
  matchNotFound: 'JOIN_NO_SYNC_MATCH_FOUND',
  maxWaitExceeded: 'MAX_WAIT_TIME_EXCEEDED',
  staleLocation: 'JOIN_STALE_LOCATION',
  noLocation: 'JOIN_NO_LOCATION',
  joinTournamentDiscountExpired: 'JOIN_TOURNAMENT_DISCOUNT_EXPIRED',
  joinTournamentDiscountExhausted: 'JOIN_TOURNAMENT_DISCOUNT_EXHAUSTED',
};

const LOCATION_ERRORS = ['JOIN_STALE_LOCATION', 'JOIN_NO_LOCATION', 'JOIN_NO_CASH_LOCATION'];

type ErrorObjectType = {
  name?: string | null;
  param?: string | null;
  message?: string | null;
};

type ErrorResponse = {
  responseObject: ErrorObjectType | ErrorObjectType[];
};

type JoinParams = {
  tournament: string; //tournament id
  device: DeviceInfo;
  playerMetadata: PlayerMetadata;
  transaction_id: string;
  viewed_ad: boolean;
  previous_match_id?: string;
  is_rematch?: boolean;
  matchId?: string;
  match_code?: string;
  geopacket?: string | null;
  instant_match_tournament_id?: string;
  cash_fee_discount?: {
    discount_percent: number;
    discount_end_time: string;
    discounted_cash_fee: number;
  };
};

const NAVIGATOR_MOCK = {};

const SKZReactUserManager = NativeModules.SKZReactUserManager;
const SKZAPIManager = NativeModules.SKZAPIManager;

const MatchJoinController = {
  isJoiningTournament: false,

  // Retry success tracking
  _retrySuccessFlag: false,
  _retrySuccessMatch: null,

  handleJoinForLiveBracket(
    position: number,
    tournament: SKZTournament,
    match: SKZMatch,
    userId: number
  ) {
    tournament = new SKZTournament(tournament);
    const roundToJoin = SKZBracket.calculateRound(position);
    if (roundToJoin == 1 && !match.tie_breaker) {
      MatchJoinController.displayJoinFlowForTournament(tournament, NAVIGATOR_MOCK);
    } else {
      MatchJoinController.displayVersusForBracket(match, userId, tournament);
    }
  },

  _handleDiscountExpiredExhaustedError(
    error: ErrorResponse,
    tournament: SKZTournament,
    proceed: () => void
  ) {
    const msg = I18n.message('Offer Expired! Would you like to play with the original entry fee?');

    const errorDialog = (
      <ErrorDialogModal
        message={msg}
        retryText={I18n.message('PROCEED')}
        hideCancel={false}
        retryCallback={() => {
          ModalNavigationController.hideModal();
          this._makeRetryJoinCall(tournament, error, proceed);
        }}
        onCancel={() => {
          NavigationManager.popNavigatorToRoot();
        }}
        onDone={() => {
          LoadingIndicator.hide();
          ModalNavigationController.hideModal();
        }}
        error={error}
      />
    );

    ModalNavigationController.showModal(errorDialog);
  },

  _makeRetryJoinCall(tournament: SKZTournament, originalError: ErrorResponse, proceed: () => void) {
    SKZReactUserManager.getUserId((error: string, userId: string) => {
      if (userId) {
        APIUtilities.createUUID().then(async (transactionID: string) => {
          const fetchDynamicDeviceInfo = SkillzModule.fetchDynamicDeviceInfo;
          SkillzModule.refreshSettingsAndAds?.(tournament?.id);

          fetchDynamicDeviceInfo(async (deviceInfo: DeviceInfo, playerMetadata: PlayerMetadata) => {
            try {
              // Create a new tournament instance with cash_fee_discount removed if error is present

              const tournamentForJoin = originalError?.responseObject[0]?.name
                ? new SKZTournament({...tournament, cash_fee_discount: undefined})
                : tournament;

              const joinParams = await this.getJoinParams(
                tournamentForJoin,
                null,
                false,
                null,
                tournament?.viewed_ad || false,
                deviceInfo,
                playerMetadata,
                transactionID,
                null,
                originalError?.responseObject[0]?.name
              );

              NetClient.postURL(
                'users/' + userId + '/matches',
                joinParams,
                SKZAPIManager.OnError.failSilently,
                (apiError: any, response: any) => {
                  if (apiError) {
                    MatchJoinController._handleDiscountExpiredExhaustedError(
                      originalError,
                      tournament,
                      proceed
                    );
                  } else {
                    MatchJoinController._retrySuccessMatch = response;
                    MatchJoinController._retrySuccessFlag = true;

                    proceed();
                  }
                }
              );
            } catch (err) {
              console.error('Error getting join params:', err);
            }
          });
        });
      } else {
        console.error('Failed to get userId:', error);
      }
    });
  },

  async displayVersusForBracket(
    match: SKZMatch,
    userId: number | string,
    tournament: SKZTournament
  ) {
    if (isAlreadyJoining(tournament)) {
      console.log('Dedupe tournament join:', tournament.id);
      return;
    }

    const currentUser: SKZUser = await CurrentUserStore.fetchUser(true);
    const players = match.players || [];
    const twoPlayersMatchOpponent = players.find(
      (player) => player?.user_id?.toString() !== userId?.toString()
    );

    setJoinSession({
      tournament,
      options: {
        allowExit: false,
        tournament: tournament,
        matchCode: null,
        viewedAd: false,
        isPlayAgain: false,
        onDone: noop,
        invitedFriends: null,
        tournamentID: tournament.id,
        isFTUE: false,
        isRematchInitiation: false,
        opponent: twoPlayersMatchOpponent,
        rematchTournamentID: null,
        matchIdToAccept: match.id,
        isConfirmedInstantMatch: false,
        match: match,
        currentUser: currentUser,
      },
    });

    // For bracket tournaments, use bracket-versus route
    if (tournament?.bracket) {
      SkillzRouter.push(`/games/${GAME_ID}/bracket-versus/${tournament.id}`, {
        tournament: tournament,
        match: match,
        userId: `${userId}`,
        viewedAd: tournament.viewed_ad,
        title: _m('BracketVersus'),
        onDone: noop,
      });
    } else {
      // For non-bracket tournaments, emit join tournament event
      GlobalEventEmitter.emit('joinTournament');
    }
  },

  displayJoinFlowForTieBreaker: async (data: {
    tournament: SKZTournament;
    userId: number;
    match: SKZMatch;
    opponent: SKZMatchPlayer;
    matchIdToAccept: string;
  }) => {
    const options: TournamentJoinOptions = {
      isFTUE: false,
      invitedFriends: null,
      rematchTournamentID: null,
      matchCode: null,
      viewedAd: false,
      isPlayAgain: false,
      userId: data.userId,
      isRematchInitiation: false,
      isTieBreaker: true,
      tournamentID: data.tournament.id,
      isMatchAccept: true,
      isHeadToHeadTie: true,
      matchIdToAccept: data.matchIdToAccept,
      opponent: data.opponent,
    };
    setJoinSession({tournament: data.tournament, options});
    await MatchJoinController.displayJoinFlowForTournamentWithCode(data.tournament, NAVIGATOR_MOCK);
  },
  displayJoinFlowForRematch: async (
    tournament: SKZTournament | SKZTournament[],
    matchIdToAccept: number | string | undefined,
    rematchTournamentId: number | undefined,
    opponent: SKZMatchPlayer
  ) => {
    if (isAlreadyJoining(tournament)) {
      console.log('Dedupe tournament join for rematch:', tournament.id);
      return;
    }

    const options: TournamentJoinOptions = {
      invitedFriends: null, // TODO (websdk-post-mvp): Add Friends support
      isPlayAgain: false,
      isBotMatch: false,
      goStraightIntoMatch: false,
      setID: undefined,
      isRematchInitiation: true,
      matchIdToAccept,
      rematchTournamentID: rematchTournamentId,
      opponent,
    };

    setJoinSession({tournament, options});

    await MatchJoinController.displayJoinFlowForTournamentWithCode(
      tournament,
      NAVIGATOR_MOCK,
      null,
      null,
      false,
      undefined,
      false,
      undefined
    );
  },
  displayJoinFlowForTournament: async (
    tournament: SKZTournament | SKZTournament[],
    _navigator: any,
    friendProfiles: SKZMatchPlayer[] | null = null,
    isPlayAgain = false,
    setID?: number, // seems to only be used for Sync matches
    isBotMatch?: boolean,
    goStraightIntoMatch = true
  ) => {
    if (isAlreadyJoining(tournament)) {
      console.log('Dedupe join flow for tournament:', tournament.id);
      return;
    }

    const matchCode = tournament.match_code ?? null;

    const options: TournamentJoinOptions = {
      invitedFriends: null, // TODO (websdk-post-mvp): Add Friends support
      isPlayAgain,
      isBotMatch,
      goStraightIntoMatch,
      setID,
    };

    setJoinSession({tournament, options});

    await MatchJoinController.displayJoinFlowForTournamentWithCode(
      tournament,
      _navigator,
      matchCode,
      friendProfiles,
      isPlayAgain,
      setID,
      isBotMatch,
      goStraightIntoMatch
    );
  },

  displayJoinFlowForTournamentWithCode: async (
    tournament: SKZTournament,
    _navigator: any,
    matchCode: string | null = null,
    friendProfiles: SKZMatchPlayer[] | null = null,
    isPlayAgain: boolean = false,
    _setID?: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _isBotMatch: boolean = false,
    goStraightIntoMatch: boolean = false
  ) => {
    if (isPooledLiquidity(tournament)) {
      // TODO (websdk-ms-4-phase-live-events): Add PooledLiquidity join flow
      console.log('WebSDK: MatchJoinController._pooledLiquidityJoinFlow() MOCK USED');
      clearJoinSession();
      return;
    }

    const isJoinable = await isTournamentJoinable(tournament);
    if (tournament === null || !isJoinable) {
      // Move user back to root but do not stop execution as per Native
      SkillzRouter.backToRoot();
    }

    if (tournament.bracket && !tournament.isLiveBracket()) {
      // TODO (websdk-post-mvp): Add Bracket tournaments
      console.log('WebSDK: MatchJoinController.displayPrizeDetailsForTournament() MOCK USED');
      const matchCodeNumber = matchCode ? parseInt(matchCode) : null;
      SkillzRouter.push(`/games/${GAME_ID}/prize-details/${tournament.id}`, {
        shouldDismissOnCompletion: false,
        tournament: tournament,
        matchCode: matchCodeNumber,
        isFromPLOW: isPlayAgain,
        isSkipRerender: true,
      });
      return;
    }

    if (tournament.live_event || isThreePlusH2H(tournament)) {
      MatchJoinController.startLiveEventFlowForTournament(tournament, matchCode);
      clearJoinSession();
      return;
    }

    await MatchJoinController.displayVersusForTournament(
      tournament,
      _navigator,
      matchCode,
      friendProfiles,
      isPlayAgain,
      goStraightIntoMatch
    );
    console.log('Unused variable: ', {
      _setID,
      _isBotMatch,
    });
  },

  startLiveEventFlowForTournament(tournament: Tournament, matchCode: string | null) {
    SkillzRouter.push(`/games/${GAME_ID}/live-event/${tournament.live_event.id}`, {
      tournamentSource: tournament,
      matchCode,
      hideOrientationFlip: true,
      isFromPlayTab: true,
      isSkipRerender: true,
    });
  },

  async startLiveBracketAutoplay(
    autoplayTournament: SKZTournament,
    mainNavigator: any = NAVIGATOR_MOCK,
    useBracketInfo: boolean = false,
    bracketInfo?: {bracketPlayerState?: string}
  ) {
    console.log('MatchJoinController:startLiveBracketAutoplay: starting live bracket autoplay');
    const canJoinMatch = isLiveBracketTournamentReadyToAutoplay(
      autoplayTournament,
      mainNavigator,
      useBracketInfo,
      bracketInfo?.bracketPlayerState
    );
    if (!canJoinMatch) {
      console.log('MatchJoinController:startLiveBracketAutoplay: bracket not ready for autoplay');
      return;
    }

    // console.log('MatchJoinController:startLiveBracketAutoplay: check location required');
    // if (autoplayTournament?.isLocationRequired()) {
    //   console.log('MatchJoinController:startLiveBracketAutoplay: location is required');
    //   const didGetLocation = await LocationPermissionController.promptForLocationAsync(
    //     false,
    //     false
    //   ).catch((error) => {
    //     console.log(
    //       'MatchJoinController:startLiveBracketAutoplay: error getting user location permission',
    //       error
    //     );
    //     return false;
    //   });

    //   if (!didGetLocation) {
    //     console.log(
    //       'MatchJoinController:startLiveBracketAutoplay: will not join the match due location permission'
    //     );
    //     return false;
    //   }
    // }

    // console.log('MatchJoinController:startLiveBracketAutoplay: got location, show modal');
    // show modal
    renderLiveBracketAutoplayDialog(autoplayTournament);

    // delay to pass control to publisher to allow the modal to shown for at least 5s before we pass control to publisher
    const timeToPassControlToPublisher = moment().add(3, 'seconds').toDate();
    console.log(
      'MatchJoinController:startLiveBracketAutoplay: pass time to publisher: ',
      timeToPassControlToPublisher
    );
    // start joining the match
    await MatchJoinController.joinLiveBracketDirectly(
      autoplayTournament,
      false,
      NAVIGATOR_MOCK,
      false,
      timeToPassControlToPublisher
    );
  },

  joinLiveBracketDirectly: async (
    tournament: SKZTournament,
    shouldCheckBalance: boolean,
    _navigator: any,
    showLoadingIndicator = false,
    timeToPassControlToPublisher: Date = new Date(-1) // todo: handle this
  ) => {
    if (isAlreadyJoining(tournament)) {
      console.log(
        'MatchJoinController: Dedupe direct tournament join for live bracket:',
        tournament.id
      );
      return;
    }

    console.log('MatchJoinController: begin validate tournament join');
    // const isValid = await MatchJoinController.validateTournamentJoin(
    //   tournament,
    //   shouldCheckBalance,
    //   NAVIGATOR_MOCK
    // );

    const isValid = true;
    console.log('MatchJoinController: completed validate tournament join');
    if (!isValid) {
      console.log('MatchJoinController: tournament seems to be invalid');
      const modalProps = {
        animationType: 'fade',
        transparent: true,
      };
      const errorDialog = (
        <ErrorDialogModal
          message={'Failed to join the match. Please try again.'}
          retryText={_m('Retry')}
          hideCancel={true}
          onDone={() => {
            LoadingIndicator.show();
            ModalNavigationController.hideModal();
            MatchJoinController.joinLiveBracketDirectly(
              tournament,
              false,
              _navigator,
              true,
              new Date(-1) // no delay to pass control to publisher
            );
            return;
          }}
        />
      );

      LoadingIndicator.hide();
      ModalNavigationController.showModal(errorDialog, modalProps);
      return false;
    }

    if (showLoadingIndicator) {
      LoadingIndicator.show();
    }

    // delay to pass control to publisher (or heading to result screen for byes) to allow the aut0-play modal to shown
    const delayToResolveMatchJoin = Math.max(
      moment(timeToPassControlToPublisher).diff(moment(), 'milliseconds'),
      0
    );

    console.log('MatchJoinController: delay to resolve match join: ', delayToResolveMatchJoin);
    await MatchJoinController.initiateTournamentJoin(
      tournament,
      null,
      false,
      null,
      false,
      async (error, match: SKZMatch) => {
        console.log('MatchJoinController: completed initiate tournament join');
        if (error || !match) {
          console.log(
            'MatchJoinController: will not join the match due error or missing match',
            error,
            match
          );

          LoadingIndicator.hide();
          ModalNavigationController.hideModal();

          return;
        }

        const currentUser: SKZUser = await CurrentUserStore.fetchUser(true);
        const userId = currentUser.id;

        if (!userId) {
          console.log('MatchJoinController: error getting user id');
          LoadingIndicator.hide();
          ModalNavigationController.hideModal();
          return;
        }

        RESTStore.clearMatchStreamCache(userId);
        PlayableMatchesStore.clearMatchSelectCache();

        console.log(
          'MatchJoinController: delay to resolve match join pause started: ',
          delayToResolveMatchJoin
        );
        await new Promise((resolve) => setTimeout(() => resolve(true), delayToResolveMatchJoin));
        console.log('MatchJoinController: delay to resolve match join completed');

        ModalNavigationController.hideModal(() => {
          if (match.didGetByeV2()) {
            // at this point, match is already instantiated, so we need to deep clone it to avoid
            // an error due modifying the original/frozen match object on `SharedPLOWBehavior`
            console.log('MatchJoinController: user did get bye');
            const matchObj = cloneDeep(match);
            const animated = false; // this is not being used on displayMatchResults
            const isSubmittingScore = true;
            const shouldPop = false;
            const onDismiss = () => {};
            const shouldShowRegularPLOW = true;

            PLOWDisplayHandler.displayMatchResults(
              matchObj,
              animated,
              isSubmittingScore,
              userId,
              shouldPop,
              onDismiss,
              shouldShowRegularPLOW
            );
          } else {
            LoadingIndicator.show();
            console.log('MatchJoinController: user ready to start a match, loader');
            const players = match.players || [];
            const twoPlayersMatchOpponent = players.find(
              (player) => player.user_id.toString() !== userId.toString()
            );
            setJoinSession({
              tournament,
              options: {
                allowExit: false,
                tournament: tournament,
                matchCode: null,
                viewedAd: false,
                isPlayAgain: false,
                onDone: noop,
                invitedFriends: null,
                tournamentID: tournament.id,
                isFTUE: false,
                isRematchInitiation: false,
                opponent: twoPlayersMatchOpponent,
                rematchTournamentID: null,
                matchIdToAccept: null,
                isConfirmedInstantMatch: true,
                instantMatchData: match,
                match: match,
                currentUser: currentUser,
              },
            });
            LoadingIndicator.hide();
            GlobalEventEmitter.emit('joinTournament');
          }
        });
      }
    );
  },

  displayEventDetailsForTournament(tournament: Tournament, matchCode: string | null) {
    if (tournament?.isLiveBracket?.()) {
      SkillzRouter.push(`/games/${GAME_ID}/brackets-v2`, {
        tournament,
        matchCode,
        hideOrientationFlip: true,
        isFromPlayTab: true,
        isSkipRerender: true,
      });
    } else {
      SkillzRouter.push(`/games/${GAME_ID}/live-event/${tournament.live_event.id}`, {
        tournamentSource: tournament,
        matchCode,
        hideOrientationFlip: true,
        isFromPlayTab: true,
        isSkipRerender: true,
      });
    }
  },

  joinLiveEvent(
    tournament: SKZTournament,
    shouldValidateBalance: boolean,
    _navigator: any,
    matchCode: string | null,
    isPlayAgain?: boolean
  ) {
    if (isPlayAgain === undefined) {
      isPlayAgain = false;
    }

    if (isAlreadyJoining(tournament)) {
      console.log('Dedupe join live event:', tournament.id);
      return;
    }

    MatchJoinController.validateTournamentJoin(
      tournament,
      shouldValidateBalance,
      NAVIGATOR_MOCK,
      (success: boolean) => {
        if (success) {
          if (ClientPropertyConstants.getLiveEventHostedEnabled()) {
            const guards = getJoinSession()?.guards;
            setJoinSession({
              tournament,
              options: {matchCode},
              guards, // this will overwrite the spread guards
            });
            GlobalEventEmitter.emit('joinTournament');
          }
        }
      }
    );
  },

  validateTournamentJoin: async (
    tournament: SKZTournament,
    shouldCheckBalance: boolean,
    _navigator: any,
    completion?: ((success: boolean) => void) | null
  ) => {
    const [user, personalInfo] = await Promise.all([
      CurrentUserStore.fetchUser(true),
      CurrentUserStore.fetchUserPersonalInfo(true),
    ]).catch(() => GlobalEventEmitter.emit('joinFailed'));

    if (!user || !personalInfo) {
      GlobalEventEmitter.emit('joinFailed');
      clearJoinSession();
      MatchJoinController.displayDefaultErrorDialog(null, _m('Cannot join tournament'), () =>
        NavigationManager.popNavigatorToRoot()
      );
      completion?.(false);
      return false;
    }

    // TODO (websdk-ms-4-phase-live-events): shouldCheckBalance is always true for H2H and Z
    // Please update the logic for other events as required
    const guards: TournamentJoinGuards = {
      balance: shouldCheckBalance && !hasEnoughBalance(tournament, user),
      account: isAccountRequired(tournament, personalInfo),
      email: isEmailRequired(tournament, user),
      phone: isPhoneRequired(tournament, personalInfo),
      age: getMinimumAge(tournament, personalInfo),
    };
    // Some trophies trigger on match join, set trophy data for toasts before that can happen,
    // No need await here since user needs to accept match and this request is not critical for the match flow
    setTrophyDataForStorage();
    const currentlyJoining = getJoinSession() || {};
    setJoinSession({...currentlyJoining, guards});
    completion?.(true);
    return true;
  },

  displayVersusForTournament: async (
    tournament: SKZTournament,
    _navigator: any,
    matchCode: string | null,
    friendProfiles: SKZMatchPlayer[] | null,
    isPlayAgain: boolean,
    goStraightIntoMatch: boolean = false,
    isDiscountedInstantMatch: boolean = false
  ) => {
    console.log('Unused variable: ', {
      matchCode,
      friendProfiles,
      isPlayAgain,
      goStraightIntoMatch,
      isDiscountedInstantMatch,
    });
    const isValid = await MatchJoinController.validateTournamentJoin(
      tournament,
      true,
      NAVIGATOR_MOCK
    );

    if (isValid) {
      // TODO (websdk-ms-1-phase-versus): Use SkillzRouter for the redirect
      // instead of GlobalEventEmitter
      // TODO (websdk-ms-1-phase-versus): Handle goStraightIntoMatch
      GlobalEventEmitter.emit('joinTournament');
    } else {
      MatchJoinController.displayDefaultErrorDialog(null, _m('Cannot join tournament'), () =>
        NavigationManager.popNavigatorToRoot()
      );
    }
  },

  initiateTournamentJoin: async (
    tournament: SKZTournament,
    matchId: string | null,
    initiatingRematch: boolean,
    matchCode: string | null,
    viewedAd: boolean,
    completion: (error: string | null, match: SKZMatch | null) => void,
    instantMatchTournamentID?: string | null
  ) => {
    const newId = await APIUtilities.createUUID();

    await MatchJoinController.initiateTournamentJoinWithTxnID(
      tournament,
      matchId,
      initiatingRematch,
      matchCode,
      viewedAd,
      newId,
      completion,
      instantMatchTournamentID
    );
  },

  initiateTournamentJoinWithTxnID: async (
    tournament: SKZTournament,
    matchId: string | null,
    initiatingRematch: boolean,
    matchCode: string | null,
    viewedAd: boolean,
    transactionID: string,
    completion: (error: string | null, match: SKZMatch | null) => void,
    instantMatchTournamentID?: string | null,
    secondTry = false
  ) => {
    GlobalEventEmitter.emit('initiateMatch');
    const userId = getUserId();
    const {deviceInfo, playerMetadata} = getDeviceInfo();

    // Dedupe matches stared without tournament session in LS:
    // - first FTUE
    // - brackets versus
    // - instamatch
    const currentlyJoining = getJoinSession();
    if (currentlyJoining?.tournament && currentlyJoining.options?.withoutSession) {
      if (currentlyJoining.tournament.id === tournament.id) {
        console.log(
          'MatchJoinController: debouncing tournament join in initiate due to withoutSession'
        );
        return;
      }
    } else {
      setJoinSession({tournament, options: {withoutSession: true}});
    }

    const joinParams: JoinParams | void = await MatchJoinController.getJoinParams(
      tournament,
      matchId,
      initiatingRematch,
      matchCode,
      viewedAd,
      deviceInfo,
      playerMetadata,
      transactionID,
      instantMatchTournamentID
    ).catch((error) => {
      console.log('MatchJoinController: Error getting join params');
      const noGeopackage = error === 'Package not installed';
      TrackMetricPerformance.addFinishedStep({
        name: 'JOIN_MATCH_FLOW',
        step: 'joinParamsCatch',
      });
      const reloadPage = () => window.location.reload();
      const retryGeoRequest = () =>
        MatchJoinController.initiateTournamentJoinWithTxnID(
          tournament,
          matchId,
          initiatingRematch,
          matchCode,
          viewedAd,
          transactionID,
          completion,
          instantMatchTournamentID,
          true
        );

      const retry = noGeopackage || secondTry ? reloadPage : retryGeoRequest;
      const cancel = () => NavigationManager.popNavigatorToRoot();

      ErrorDialogModal.displayWithRetry(retry, cancel, null, {message: error});
    });
    TrackMetricPerformance.addFinishedStep({
      name: 'JOIN_MATCH_FLOW',
      step: 'getJoinParams',
    });
    if (!joinParams) {
      console.log('MatchJoinController: join params not available');
      return;
    }

    const path =
      tournament.isLiveBracket() && tournament.bracket?.id
        ? `/v2/live-brackets/${tournament.bracket.id}/join`
        : matchId && !initiatingRematch
          ? `users/${userId}/matches/${matchId}/players`
          : `users/${userId}/matches`;

    await MatchJoinController.attemptFinalJoinAPICall(
      tournament,
      path,
      joinParams,
      userId,
      false,
      completion
    );
  },

  getJoinParams: async (
    tournament: SKZTournament,
    matchId: string | null,
    initiatingRematch: boolean,
    matchCode: string | null,
    viewedAd: boolean,
    deviceInfo: DeviceInfo,
    playerMetadata: PlayerMetadata,
    transactionID: string,
    instantMatchTournamentID?: string | null,
    error?: string
  ) => {
    const geopacket = tournament.isLocationRequired()
      ? await GeocomplyController.getGeoPacket()
      : null;

    const joinParams: JoinParams = {
      tournament: tournament.id,
      device: deviceInfo,
      playerMetadata: playerMetadata,
      transaction_id: transactionID,
      viewed_ad: viewedAd,
      previous_match_id: undefined,
      is_rematch: undefined,
      matchId: undefined,
      match_code: undefined,
      geopacket,
      instant_match_tournament_id: undefined,
    };

    if (matchId && initiatingRematch) {
      joinParams.previous_match_id = matchId;
      joinParams.is_rematch = true;
    } else if (matchId && tournament.friend) {
      joinParams.matchId = matchId;
    }

    if (matchCode) {
      joinParams.match_code = matchCode;
    }

    if (instantMatchTournamentID) {
      joinParams.instant_match_tournament_id = instantMatchTournamentID;
    }

    if (tournament.cash_fee_discount) {
      joinParams.cash_fee_discount = error ? undefined : tournament.cash_fee_discount;
    }

    return joinParams;
  },

  attemptFinalJoinAPICall: async (
    tournament: SKZTournament,
    path: string,
    joinParams: JoinParams,
    userId: string,
    shouldSkipRetry: boolean,
    completion: ((error: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const callback = (joinErr: ErrorResponse | undefined, payload: SKZMatch | undefined): void => {
      TrackMetricPerformance.addFinishedStep({
        name: 'JOIN_MATCH_FLOW',
        step: 'successcallback',
        attributes: {
          path: path,
          error: joinErr,
          payload,
        },
      });
      if (MatchJoinController._retrySuccessFlag && MatchJoinController._retrySuccessMatch) {
        const retryResponse = MatchJoinController._retrySuccessMatch;
        MatchJoinController._retrySuccessFlag = false;
        MatchJoinController._retrySuccessMatch = null;

        MatchJoinController.handleFinalJoinAPIResponse(
          tournament,
          null,
          retryResponse as SKZMatch,
          path,
          joinParams,
          userId,
          shouldSkipRetry,
          completion
        );
        return;
      } else {
        MatchJoinController.handleFinalJoinAPIResponse(
          tournament,
          joinErr as ErrorResponse,
          payload as SKZMatch,
          path,
          joinParams,
          userId,
          shouldSkipRetry,
          completion
        );
      }
    };

    const retry = true;

    // The matchId check exists for handling friends tournament tie breakers.
    // If the friends tournament is a tie breaker, it will have a matchId for the new match
    // and we need to let it fall through to the usual tie breaker join flow
    if (
      tournament.friend &&
      joinParams.match_code &&
      !joinParams.matchId &&
      tournament.isIncomingInvite()
    ) {
      console.log('WebSDK: FriendsTournamentApiController.joinTournament() MOCK USED');
    } else if (tournament.friend && !joinParams.matchId) {
      console.log('WebSDK: FriendsTournamentApiController.createAndJoinTournament() MOCK USED');
    } else {
      console.log('MatchJoinController: attemptFinalJoinAPICall');
      NetClient.postURL(path, joinParams, retry, callback);
    }
  },
  handleFinalJoinAPIResponse: (
    tournament: Tournament,
    joinErr: ErrorResponse | null,
    payload: SKZMatch | null,
    path: string,
    joinParams: JoinParams,
    userId: string,
    shouldSkipRetry: boolean,
    completion: ((error: string | null, match: SKZMatch | null) => void) | null
  ) => {
    if (joinErr) {
      console.log('MatchJoinController: error in handleFinalJoinAPIResponse', joinErr);
      MatchJoinController.handleJoinError(
        tournament,
        joinErr,
        path,
        joinParams,
        userId,
        shouldSkipRetry,
        completion
      );
    } else if (!payload?.id || !payload?.tournament_id) {
      console.log('MatchJoinController: empty match in handleFinalJoinAPIResponse', joinErr);
      AWSManager.trackEvent('MATCH_JOIN_CALL_COMPLETE_EMPTY_MATCH', {
        result: JSON.stringify({received_match: payload, joinParams: joinParams}),
      });
      MatchJoinController.handleJoinError(
        tournament,
        // format it like an ErrorResponse so _handleJoinError doesn't have to figure out the type
        {responseObject: [{name: JOIN_ERRORS.emptyMatch, param: '', message: ''}]},
        path,
        joinParams,
        userId,
        shouldSkipRetry,
        completion
      );
    } else {
      const match = new SKZMatch(payload);
      MatchJoinController.prepareForControlPassingPostSuccessfulJoin(match, userId, completion);
    }
  },

  prepareForControlPassingPostSuccessfulJoin: (
    match: SKZMatch,
    userId: string,
    completion: ((error: string | null, matchComp: SKZMatch | null) => void) | null
  ) => {
    const userPlayer = match.getUserPlayer(parseInt(userId));
    if (userPlayer) {
      const tournamentPlayerId = userPlayer.id.toString();
      const matchId = match.id.toString();

      Preferences.set({
        [LegacyEntryIdKey]: userPlayer.entry_id,
        [LegacyTournamentIdKey]: matchId,
        [LegacyTournamentPlayerIdKey]: tournamentPlayerId,
        [LegacyPlayerIdKey]: userId,
      });

      if (match.type !== 'TURN_BASED') {
        completion?.(null, match);
      }
    } else {
      completion?.(null, match);
      AWSManager.trackEvent('JOINING_MATCH_PLAYER_NOT_FOUND', {result: JSON.stringify(match)});
      console.warn(
        'MatchJoinController: [POST JOIN] User: ' +
          userId +
          ' not found in match:' +
          JSON.stringify(match)
      );
    }
  },
  passControlToPublisherForJoin: (tournament: SKZTournament, match: SKZMatch) => {
    const shouldCombineMedals =
      tournament?.hasMedals() && ClientPropertyConstants.getLeaguesCombinedMedalsEnabled();
    const matchInstance = match instanceof SKZMatch ? match : new SKZMatch(match);

    let leagueType;
    if (shouldCombineMedals) {
      leagueType = LeagueType.MEDAL;
    } else if (tournament.isCashTournament()) {
      leagueType = LeagueType.CASH;
    } else {
      leagueType = LeagueType.Z;
    }

    try {
      writeLeaguePlacement(leagueType);
    } catch (e) {
      console.warn(e);
    }
    CurrentUserStore.clearUserCache();
    LiveEventFTUEFlowController.resetState();
    MatchJoinController.setupUnfinishedGameNotification(matchInstance);
    OrientationTransitionController.updateOrientationToLandscape();

    let currentUser: string;
    try {
      currentUser = getUserId();
    } catch (e) {
      currentUser = '0';
    }
    const awsObj = matchInstance.createAWSMatchObjectFromMatch(parseInt(currentUser));
    console.log('SDK_MATCH_JOIN', awsObj);
    AWSManager.trackEvent('SDK_MATCH_JOIN', awsObj);

    // Save the tournament to local preferences if it is a H2H cash match
    if (
      tournament.isCashEntry() &&
      !tournament.isBracket() &&
      !tournament.live_event &&
      !tournament.synchronous
    ) {
      Preferences.set({[SKZConstants.LAST_PLAYED_CASH_TOURNAMENT]: JSON.stringify(tournament)});
    }

    // Save the tournament to local preferences if it is a LiveEvent
    if (tournament.live_event && !tournament.synchronous) {
      Preferences.set({[SKZConstants.LAST_PLAYED_LIVE_EVENT]: JSON.stringify(tournament)});
    }

    if (!tournament.isZTournament()) {
      SKAdNetworkController.triggerAdNetworkConversion(
        SKZConstants.AdNetworkConversionTypes.FIRST_CASH_MATCH
      );
    } else {
      SKAdNetworkController.triggerAdNetworkConversion(
        SKZConstants.AdNetworkConversionTypes.MATCH_ENTERED
      );
    }

    GlobalEventEmitter.emit('startMatch', {tournament, match: matchInstance});
  },
  setupUnfinishedGameNotification: (match: SKZMatch | null) => {
    if (!match) {
      return;
    }
    const matchExpireDate = new Date(match.expires_at).getTime();
    const mSecUntilExpire = matchExpireDate - new Date().getTime();
    const minutesToWarn = ClientPropertyConstants.getLocalNotifAbortTime();
    const mSecToWarn = minutesToWarn * SKZConstants.MS_PER_MIN;
    const mSecUntilNotify = mSecUntilExpire - mSecToWarn;
    if (mSecUntilNotify > 0) {
      SkillzModule.scheduleUnfinishedGameNotification(mSecUntilNotify);
    }
  },

  handleJoinError: (
    tournament: SKZTournament,
    error: ErrorResponse,
    path: any,
    joinParams: JoinParams,
    userId: string,
    shouldSkipRetry: boolean,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const errorName =
      (error &&
        error.responseObject &&
        error.responseObject instanceof Array &&
        error.responseObject[0]?.name) ||
      'Network failure';

    if (shouldSkipRetry) {
      LoadingIndicator.hide();
      if (errorName == JOIN_ERRORS.emptyMatch) {
        AWSManager.trackEvent('MATCH_JOIN_EMPTY_MATCH_RETRY_STILL_EMPTY', {
          result: JSON.stringify(joinParams),
        });
        MatchJoinController.handleJunkMatchForTournament(tournament, userId, completion);
        return;
      }
      // No retry at this point, show error and bail out.
      const errorMessage =
        (error &&
          error.responseObject &&
          error.responseObject instanceof Array &&
          error?.responseObject?.[0]?.message) ||
        null;
      MatchJoinController.displayDefaultErrorDialog(error, errorMessage, completion);
    } else {
      if (MatchJoinController.isLocationError(errorName)) {
        MatchJoinController.handleLocationError(
          tournament,
          error,
          path,
          joinParams,
          userId,
          completion
        );
        if (NativeModules.RNGeoComplySdkWrapperModule) {
          return;
        }
      } else if (MatchJoinController.isMaxUsersError(errorName)) {
        MatchJoinController.handleJoinNoRetry(error, errorName as string, completion);
      } else if (errorName == JOIN_ERRORS.unavailable) {
        MatchJoinController.handleJoinErrorUnavailable(error, errorName, completion);
      } else if (
        errorName === JOIN_ERRORS.joinTournamentDiscountExpired ||
        errorName === JOIN_ERRORS.joinTournamentDiscountExhausted
      ) {
        MatchJoinController._handleDiscountExpiredExhaustedError?.(error, tournament, () => {
          MatchJoinController.attemptFinalJoinAPICall?.(
            tournament,
            path,
            joinParams,
            userId,
            false,
            completion
          );
        });
      } else if (errorName == JOIN_ERRORS.emptyMatch) {
        MatchJoinController.attemptFinalJoinAPICall(
          tournament,
          path,
          joinParams,
          userId,
          true,
          completion
        );
      } else if (errorName == JOIN_ERRORS.needsRegistration) {
        MatchJoinController.handleJoinRegistrationError((verified: boolean) => {
          if (verified) {
            MatchJoinController.attemptFinalJoinAPICall(
              tournament,
              path,
              joinParams,
              userId,
              false,
              completion
            );
          } else {
            NavigationManager.popNavigatorToRoot();
          }
        });
      } else if (errorName == JOIN_ERRORS.alreadyPlaying) {
        NavigationManager.popNavigatorToRoot();
        SyncMatchMakingController.displayTournamentPendingModal();
        LoadingIndicator.hide();
      } else if (errorName == JOIN_ERRORS.parameterValidation && tournament?.friend) {
        // This is current handling for the friends tournament case where the tournament cannot be joined due to it being full
        // This is to prevent users from seeing the ugly parameter validation error if they tried to join at the same time as someone who filled the tournament
        console.debug(
          '"MatchJoinController: parameter validation error on friend tournament join, rerouting to join unavailable flow: ',
          JSON.stringify(error)
        );
        MatchJoinController.handleJoinErrorUnavailable(error, errorName, completion);
      } else if (errorName == JOIN_ERRORS.tournamentFull) {
        MatchJoinController.handleJoinErrorTournamentFull(
          tournament,
          error,
          path,
          joinParams,
          userId,
          completion
        );
      } else {
        MatchJoinController.checkJoinError(tournament, error, path, joinParams, userId, completion);
      }
    }
  },
  setIsJoiningTournament: (isJoining: boolean) => {
    console.log('WebSDK: MatchJoinController.setIsJoiningTournament()', isJoining);
    if (!isJoining) {
      clearJoinSession();
    }
  },
  isMaxUsersError: (errorName: string) => {
    return (
      !!errorName &&
      errorName.toUpperCase &&
      errorName.toUpperCase().includes('LIVE_EVENT_MAXIMUM_PLAYERS_REACHED')
    );
  },
  isLocationError: (errorName: string) => {
    return (
      !!errorName &&
      errorName.toUpperCase &&
      errorName.toUpperCase().includes('LOCATION') &&
      !errorName.toUpperCase().includes('JOIN_NO_CASH_LOCATION') &&
      !errorName.toUpperCase().includes('JOIN_OUTSIDE_LOCATION')
    );
  },
  handleJunkMatchForTournament: (
    tournament: SKZTournament,
    userId: string,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    AWSManager.trackEvent('MATCH_JOIN_SHOWING_EMPTY_MATCH_ERROR', {
      result: JSON.stringify({tournament}),
    });
    const buttons = [_m('Back')];
    if (tournament.isPointsTournament()) {
      buttons.push(_m('Get Support'));
    }
    const modal = (
      <StaticWarningModal
        message={_m(
          'Sorry we were unable to start your match. Please try again and contact support about a refund if you were charged.'
        )}
        buttonTitleArray={buttons}
        callback={(index: number) => {
          MatchJoinController.handleJunkMatchModalButtons(index, tournament, userId, completion);
        }}
      />
    );
    ModalNavigationController.showModal(modal, undefined);
  },

  handleJunkMatchModalButtons: (
    buttonIndex: number,
    tournament: SKZTournament,
    userId: string,
    completion: ((joinError: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const supportIndex = 1;

    if (buttonIndex == supportIndex) {
      MatchJoinController.goToSupport(tournament, userId);
    }

    completion?.(JOIN_ERRORS.emptyMatch, null);
    ModalNavigationController.hideModal(() => NavigationManager.popNavigatorToRoot());
  },
  goToSupport: (tournament: SKZTournament, userId: string) => {
    console.log('WebSDK: MatchJoinController.goToSupport() MOCK USED', {
      tournament,
      userId,
    });
    // TODO: Add go to support
    // const props = {
    //   matchId: `7777${tournament.id}7777`,
    //   initialFunction: 5,
    //   isMatchIDEditable: false,
    // };
    // NativeNavigation.gotoContactUs({...props, reportedPlayerId: userId});
  },

  handleLocationError: (
    tournament: Tournament,
    error: ErrorResponse,
    path: any,
    joinParams: JoinParams,
    userId: string,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const errorName =
      error.responseObject instanceof Array
        ? error?.responseObject?.[0]?.name
        : error?.responseObject?.name;
    if (LOCATION_ERRORS.includes(errorName as string)) {
      GeocomplyController.resetGeoPacket().then((geoPacket) => {
        joinParams.geopacket = geoPacket;
        MatchJoinController.attemptFinalJoinAPICall(
          tournament,
          path,
          joinParams,
          userId,
          true,
          completion
        );
      });
      return;
    }
    ErrorDialogModal.displayWithRetry(
      (): void => {
        GeocomplyController.resetGeoPacket();
      },
      () => {
        const message = _m(
          'Due to legal restrictions, you must enable location services for this game in your Device Settings in order to play for real money.'
        );
        MatchJoinController.displayDefaultErrorDialog(error, message, completion);
        NavigationManager.popEverythingToRoot();
      },
      null,
      (error?.responseObject as Array<ErrorObjectType>)[0]?.name
    );
  },
  displayDefaultErrorDialog: (
    error: ErrorResponse | null,
    msg: string | null,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const errorName =
      error && error.responseObject instanceof Array && error.responseObject[0]?.name
        ? error.responseObject[0].name
        : 'Network failure';
    Alert.alert(_m('Error'), msg ?? '', [
      {
        text: _m('OK'),
        onPress: () => {
          if (completion) {
            completion(errorName, null);
          }
        },
      },
    ]);
  },

  handleJoinNoRetry: (
    error: ErrorResponse,
    errorName: string | null,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    console.debug('MatchJoinController: _handleJoinNoRetry for error: ' + errorName);
    const errorMessage =
      (error &&
        error.responseObject &&
        error.responseObject instanceof Array &&
        error?.responseObject?.[0]?.message) ||
      null;

    const modalProps = {
      animationType: 'fade',
      transparent: true,
    };
    const errorDialog = (
      <ErrorDialogModal
        message={errorMessage}
        retryText={_m('OK')}
        hideCancel={true}
        retryCallback={() => {
          /* */
        }}
        onDone={() => {
          LoadingIndicator.hide();
          ModalNavigationController.hideModal(() => NavigationManager.popNavigatorToRoot());
          completion?.(errorName ?? 'Err', null);
        }}
        error={error}
      />
    );

    ModalNavigationController.showModal(errorDialog, modalProps);
  },

  handleJoinErrorUnavailable: (
    error: ErrorResponse,
    errorName: string,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    console.debug('MatchJoinController: JOIN_ERROR_UNAVAILABLE');
    let msg = _m(
      'Sorry! This tournament is no longer available. Please try a different tournament.'
    );
    if (error?.responseObject?.[0]?.param === 'invitedUsers') {
      msg = _m(
        'Unable to text this phone number, please select a different contact or update the selected contact.'
      );
    }

    MatchJoinController.displayDefaultErrorDialog(error, msg, completion);
  },

  handleJoinRegistrationError: (callback: (verified: boolean) => void) => {
    LoadingIndicator.hide();

    const ageVerifyProps = {
      minimumAge: CASH_MINIMUM_AGE,
      titleText: _m('Enter Your Date of Birth'),
    };
    const saveAccountProps = {
      shouldCheckEmail: true,
      allowExit: true,
      allowSignIn: false,
    };
    const emailVerifyProps = {
      shouldCheckEmailVerify: true,
      shouldShowOTANavBar: true,
    };
    MatchJoinController.assignMainUserFields()
      .then((res) => {
        if (res) {
          const {currentUser, currentUserPersonalInfo} = res;
          if (!currentUserPersonalInfo || !currentUser) {
            console.warn('MatchJoinController: missing user information');
            callback(false);
            return;
          }
          SaveAccountFlowController.displaySaveAccountFlowWithUserInfo(
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            currentUserPersonalInfo,
            currentUser,
            null,
            false,
            callback
          );
        }
      })
      .catch((e: string) => {
        console.warn('MatchJoinController: error completing full verify', e);
        callback(false);
      });
  },

  handleJoinErrorTournamentFull: (
    tournament: Tournament,
    error: ErrorResponse,
    attemptedPath: string,
    attemptedJoinParams: any,
    attemptedUserId: any,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    console.debug('MatchJoinController: TOURNAMENT_FULL');
    const errorMessage =
      error && error.responseObject && error.responseObject instanceof Array
        ? error?.responseObject?.[0]?.message
        : null;

    if (attemptedJoinParams.instant_match_tournament_id && completion) {
      // Should not use standard error modal for InstaMatch
      completion(errorMessage as string, null);
      return;
    }

    MatchJoinController.checkJoinError(
      tournament,
      error,
      attemptedPath,
      attemptedJoinParams,
      attemptedUserId,
      completion
    );
  },

  async assignMainUserFields(): Promise<
    | {
        currentUser: any;
        currentUserPersonalInfo: any;
      }
    | undefined
  > {
    try {
      const currentUser = await CurrentUserStore.fetchUser(true);
      const currentUserPersonalInfo = await CurrentUserStore.fetchUserPersonalInfo(true);
      return {
        currentUser,
        currentUserPersonalInfo,
      };
    } catch (e) {
      console.warn('MatchJoinController: Error while retrieving user info');
      console.warn(e);
    }
  },
  checkJoinError: (
    tournament: Tournament,
    error: ErrorResponse,
    attemptedPath: string,
    attemptedJoinParams: any,
    attemptedUserId: any,
    completion: ((err: string | null, match: SKZMatch | null) => void) | null
  ) => {
    const errorName =
      (error.responseObject instanceof Array && error?.responseObject?.[0]?.name) ||
      'Network failure';
    if (MatchJoinController.isLocationError(errorName)) {
      MatchJoinController.handleLocationError(
        tournament,
        error,
        attemptedPath,
        attemptedJoinParams,
        attemptedUserId,
        completion
      );
      return;
    } else if (MatchJoinController.isMaxUsersError(errorName as string)) {
      MatchJoinController.handleJoinNoRetry(error, errorName as string, completion);
      return;
    } else if (
      errorName === JOIN_ERRORS.cashDisabledLocation ||
      errorName === JOIN_ERRORS.vpnOrProxy
    ) {
      MatchJoinController.handleJoinNoRetry(
        error,
        (error.responseObject instanceof Array && error?.responseObject?.[0]?.message) || null,
        completion
      );
      return;
    }

    MatchJoinController.handleJoinRetry(
      tournament,
      error,
      errorName as string,
      attemptedPath,
      attemptedJoinParams,
      attemptedUserId,
      completion
    );
  },
  handleJoinRetry: (
    tournament: Tournament,
    error: ErrorResponse,
    errorName: string,
    attemptedPath: string,
    attemptedJoinParams: any,
    attemptedUserId: any,
    completion: any
  ) => {
    console.debug('MatchJoinController: _handleJoinRetry for error: ' + errorName);
    let errorMessage =
      (error &&
        error.responseObject &&
        error.responseObject instanceof Array &&
        error?.responseObject?.[0]?.message) ||
      null;
    if (errorName === INVALID_USER_SEGMENT) {
      errorMessage = _m(
        'Unable to challenge this player. Please select a different friend and try again.'
      );
    }
    ErrorDialogModal.displayWithRetry(
      async (): Promise<void> => {
        MatchJoinController.attemptFinalJoinAPICall(
          tournament,
          attemptedPath,
          attemptedJoinParams,
          attemptedUserId,
          false,
          completion
        );
        return;
      },
      () => {
        LoadingIndicator.hide();
        completion(errorName, null);
      },
      null,
      {message: errorMessage}
    );
  },

  joinBracketMatchDirectly(tournament: SKZTournament, shouldValidateBalance: boolean) {
    if (isAlreadyJoining(tournament)) {
      console.log('Dedupe join bracket match directly:', tournament.id);
      return;
    }

    MatchJoinController.validateTournamentJoin(
      tournament,
      shouldValidateBalance,
      NAVIGATOR_MOCK,
      (success: boolean) => {
        if (success) {
          LoadingIndicator.show();
          MatchJoinController.initiateTournamentJoin(
            tournament,
            null,
            false,
            null,
            false,
            (error: string | null, match: SKZMatch | null) => {
              LoadingIndicator.hide();
              if (error || !match) {
                SkillzRouter.back();
              } else {
                const userId = getUserId();
                RESTStore.clearMatchStreamCache(userId);
                PlayableMatchesStore.clearMatchSelectCache();
                MatchJoinController.passControlToPublisherForJoin(tournament, match);
              }
            }
          );
        }
      }
    );
  },

  registerForLiveBracket(
    tournament: SKZTournament,
    shouldValidateBalance: boolean,
    _navigator: any,
    completion: (success: boolean) => void
  ) {
    MatchJoinController.validateTournamentJoin(
      tournament,
      shouldValidateBalance,
      NAVIGATOR_MOCK,
      (success: boolean) => {
        // Not joining the game actually
        // TODO: handle join guards
        clearJoinSession();
        console.debug('MatchJoinController: validateTournamentJoin:' + JSON.stringify(tournament));
        if (!success || !tournament?.bracket?.id) {
          completion(false);
          return;
        }

        LoadingIndicator.show();
        const url = 'brackets/' + tournament.bracket.id + '/players';
        const retryMode = true;

        NetClient.postURL(
          url,
          tournament?.viewed_ad ? {viewed_ad: true} : null,
          retryMode,
          (error: unknown | null, payload: unknown | null) => {
            LoadingIndicator.hide();
            if (error) {
              console.log('!!! MatchJoinController: error on join:' + JSON.stringify(error));
              MatchJoinController.displayDefaultErrorDialog(
                null,
                _m('Cannot join tournament at this moment. Please try again.'),
                () => completion(false)
              );
            } else {
              console.log('!!! MatchJoinController: successful join:' + JSON.stringify(payload));
              completion(true);
            }
          }
        );
      }
    );
  },
};

export default MatchJoinController;
